/**
 * Do lobby para a simulação: perfil de cada assento e recordes locais.
 *
 * A linhagem do tanque vira multiplicadores pequenos (±~10%) a partir do
 * envelope de stats da família — a mesma fonte que orienta o catálogo — e o
 * poder escolhido vem do pool da família. Poder sempre no nível 1: no sofá
 * os dois jogam de igual para igual, sem vantagem de quem tem tanque elite.
 */
import { TANK_FAMILIES } from "@/lib/data/tankFamilies";
import type { TankFamilyId } from "@/lib/types/tanks";
import type { SofaDifficulty, SofaSeatSetup, SofaSpecial } from "./sofaSim";

const SUPPORTED: ReadonlySet<string> = new Set<SofaSpecial>([
  "shield",
  "regen",
  "stealth",
  "explosive",
  "piercing",
  "homing",
]);

/** Poderes da família que o sofá implementa, na ordem do pool. */
export function familySpecials(family: TankFamilyId): SofaSpecial[] {
  const pool = TANK_FAMILIES[family]?.specialsPool ?? [];
  return pool.filter((s): s is SofaSpecial => SUPPORTED.has(s));
}

/** Multiplicador a partir da fração do envelope (1/3 = neutro). */
function profileMul(fraction: number): number {
  return Math.round((1 + (fraction - 1 / 3) * 0.9) * 100) / 100;
}

export function seatSetupFor(family: TankFamilyId, specialIndex: number): SofaSeatSetup {
  const profile = TANK_FAMILIES[family]?.profile ?? { speed: 1 / 3, damage: 1 / 3, health: 1 / 3 };
  const specials = familySpecials(family);
  const special =
    specials.length > 0
      ? specials[((specialIndex % specials.length) + specials.length) % specials.length]
      : null;
  return {
    speedMul: profileMul(profile.speed),
    damageMul: profileMul(profile.damage),
    healthMul: profileMul(profile.health),
    special,
    specialPower: 1,
  };
}

// ─── Recordes (por aparelho) ─────────────────────────────────────────────────

const RECORDS_KEY = "rocketanks_sofa_records_v1";

/**
 * Onda máxima do coop por dificuldade e número de jogadores. A chave da
 * dupla é só a dificuldade ("normal") — o formato antigo continua valendo;
 * com mais gente vira "normal@3", "normal@4".
 */
export type SofaRecords = Record<string, number>;

export function recordKey(difficulty: SofaDifficulty, players = 2): string {
  return players === 2 ? difficulty : `${difficulty}@${players}`;
}

export function recordFor(records: SofaRecords, difficulty: SofaDifficulty, players = 2): number {
  return records[recordKey(difficulty, players)] ?? 0;
}

export function loadRecords(): SofaRecords {
  try {
    const raw = localStorage.getItem(RECORDS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: SofaRecords = {};
    for (const [k, v] of Object.entries(parsed)) if (Number(v) > 0) out[k] = Number(v);
    return out;
  } catch {
    return {};
  }
}

/** Salva a onda alcançada no coop; devolve true se for recorde novo. */
export function saveRecord(difficulty: SofaDifficulty, wave: number, players = 2): boolean {
  const records = loadRecords();
  const key = recordKey(difficulty, players);
  if (wave <= (records[key] ?? 0)) return false;
  records[key] = wave;
  try {
    localStorage.setItem(RECORDS_KEY, JSON.stringify(records));
  } catch {
    // armazenamento indisponível: o recorde vale só nesta sessão
  }
  return true;
}
